/**
 * Production Configuration
 * Handles environment variable loading and configuration for production deployment
 */

// Helper function to safely access environment variables
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
      return import.meta.env[key] as string;
    }
  } catch (error) {
    console.warn(`⚠️  Could not access environment variable: ${key}`, error);
  }
  
  return defaultValue;
};

// Safe access to import.meta.env.MODE
const getMode = (): string => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE) {
      return import.meta.env.MODE as string;
    }
  } catch (error) {
    // Fallback to checking build artifacts or default
  }
  return 'development';
};

// Environment detection
export const ENV = {
  // Check if running in production mode
  isProduction: getMode() === 'production',
  
  // Check if running in development mode
  isDevelopment: getMode() === 'development',
  
  // Check if running in Figma Make environment
  isFigmaMake: typeof window !== 'undefined' && (
    window.location.hostname.includes('figma.com') || 
    window.location.hostname.includes('figma.engineering') ||
    window.location.hostname.includes('esm.sh')
  ),
                
  // Get current mode
  mode: getMode()
};

// Backend Configuration
export const BACKEND_CONFIG = {
  // Backend URL from environment variable or default to localhost
  url: getEnvVar('VITE_BACKEND_URL', 'http://localhost:5001'),
  
  // API endpoint - use VITE_API_URL if provided, otherwise append /api to backend URL
  get apiUrl() {
    const apiUrl = getEnvVar('VITE_API_URL', '');
    if (apiUrl) return apiUrl;
    
    // If VITE_BACKEND_URL already has /api, use it as is
    if (this.url.endsWith('/api')) return this.url;
    
    // Otherwise append /api
    return `${this.url}/api`;
  },
  
  // Socket URL - use VITE_SOCKET_URL if provided, otherwise use backend URL without /api
  get socketUrl() {
    const socketUrl = getEnvVar('VITE_SOCKET_URL', '');
    if (socketUrl) return socketUrl;
    
    // Remove /api suffix if present
    const baseUrl = this.url.replace(/\/api$/, '');
    return baseUrl;
  },
  
  // Timeout for API requests (30 seconds)
  timeout: 30000,
  
  // Retry configuration
  retry: {
    maxAttempts: 3,
    delay: 1000
  }
};

// Demo Mode Configuration
export const DEMO_MODE_CONFIG = {
  // Enable/disable demo mode from environment variable
  enabled: getEnvVar('VITE_DEMO_MODE', 'false') === 'true',
  
  // Force demo mode in Figma Make if backend URL not provided
  get shouldUseDemoMode() {
    if (this.enabled) return true;
    if (ENV.isFigmaMake && !getEnvVar('VITE_BACKEND_URL')) return true;
    return false;
  }
};

// Payment Gateway Configuration
export const PAYMENT_CONFIG = {
  razorpay: {
    keyId: getEnvVar('VITE_RAZORPAY_KEY_ID', ''),
    currency: 'INR',
    name: 'MatrimonyApp',
    description: 'Subscription Payment',
    theme: {
      color: '#E91E63'
    }
  }
};

// Firebase Configuration (for push notifications)
export const FIREBASE_CONFIG = {
  apiKey: getEnvVar('VITE_FIREBASE_API_KEY', ''),
  authDomain: getEnvVar('VITE_FIREBASE_AUTH_DOMAIN', ''),
  projectId: getEnvVar('VITE_FIREBASE_PROJECT_ID', ''),
  storageBucket: getEnvVar('VITE_FIREBASE_STORAGE_BUCKET', ''),
  messagingSenderId: getEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID', ''),
  appId: getEnvVar('VITE_FIREBASE_APP_ID', ''),
  measurementId: getEnvVar('VITE_FIREBASE_MEASUREMENT_ID', ''),
  vapidKey: getEnvVar('VITE_FIREBASE_VAPID_KEY', '')
};

// App Configuration
export const APP_CONFIG = {
  name: 'Atootbandan',
  version: '1.0.0',
  description: 'Professional Matrimonial Platform',
  
  // Feature flags
  features: {
    pushNotifications: !!FIREBASE_CONFIG.apiKey,
    truecallerLogin: true,
    otpLogin: true,
    emailLogin: true,
    socialLogin: false, // Google, Facebook login (not implemented yet)
    chatFeature: true,
    premiumPlans: true,
    walletSystem: true,
    referralSystem: true
  }
};

// Logging Configuration
export const LOGGING_CONFIG = {
  // Enable detailed logging in development
  verbose: ENV.isDevelopment,
  
  // Log API requests
  logRequests: ENV.isDevelopment,
  
  // Log errors to console
  logErrors: true,
  
  // Log warnings
  logWarnings: true
};

/**
 * Validates the configuration and logs warnings for missing values
 */
export const validateConfig = () => {
  try {
    const warnings: string[] = [];
    const errors: string[] = [];
    
    // Only log config checks in development mode
    if (!ENV.isProduction) {
      // console.log('🔧 Production Configuration Check:');
      // console.log('=====================================');
      
      // Environment
      // console.log(`📍 Mode: ${ENV.mode}`);
      // console.log(`📍 Is Production: ${ENV.isProduction}`);
      // console.log(`📍 Is Figma Make: ${ENV.isFigmaMake}`);
    
      // Backend Configuration
      // console.log(`\n🔗 Backend Configuration:`);
      // console.log(`   Base URL: ${BACKEND_CONFIG.url}`);
      // console.log(`   API URL: ${BACKEND_CONFIG.apiUrl}`);
      // console.log(`   Socket URL: ${BACKEND_CONFIG.socketUrl}`);
      
      // Environment Variables
      // console.log(`\n🔧 Environment Variables:`);
      // console.log(`   VITE_BACKEND_URL: ${getEnvVar('VITE_BACKEND_URL') || 'NOT SET'}`);
      // console.log(`   VITE_API_URL: ${getEnvVar('VITE_API_URL') || 'NOT SET'}`);
      // console.log(`   VITE_SOCKET_URL: ${getEnvVar('VITE_SOCKET_URL') || 'NOT SET'}`);
    }
  
  if (!getEnvVar('VITE_BACKEND_URL') && !getEnvVar('VITE_API_URL') && !DEMO_MODE_CONFIG.enabled) {
    // Only show warning if not on localhost
    const isLocalhost = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1'
    );
    
    if (!isLocalhost) {
      warnings.push('VITE_BACKEND_URL or VITE_API_URL not set and not on localhost');
    }
  }
  
  if (!ENV.isProduction) {
    // Demo Mode
    // console.log(`\n🎭 Demo Mode: ${DEMO_MODE_CONFIG.shouldUseDemoMode ? 'ENABLED' : 'DISABLED'}`);
    if (DEMO_MODE_CONFIG.shouldUseDemoMode) {
      // console.log('   ⚠️  Running in demo mode - no backend connection required');
      // console.log('   📌 To disable: Set VITE_DEMO_MODE=false in .env');
    }
    
    // Payment Configuration
    // console.log(`\n💳 Payment Configuration:`);
    if (PAYMENT_CONFIG.razorpay.keyId) {
      // console.log(`   ✅ Razorpay configured`);
    } else {
      // warnings.push('VITE_RAZORPAY_KEY_ID not set. Payment features will not work.');
      // console.log('   ⚠️  Razorpay not configured');
    }
    
    // Firebase Configuration
    // console.log(`\n🔔 Push Notifications:`);
    if (APP_CONFIG.features.pushNotifications) {
      // console.log(`   ✅ Firebase configured`);
    } else {
      // console.log('   ⚠️  Firebase not configured (optional)');
    }
    
    // Features
    // console.log(`\n✨ Features Enabled:`);
    Object.entries(APP_CONFIG.features).forEach(([feature, enabled]) => {
      // console.log(`   ${enabled ? '✅' : '❌'} ${feature}`);
    });
  }
  
  // Display warnings
  if (warnings.length > 0 && !ENV.isProduction) {
    // console.log(`\n⚠️  Configuration Notes (${warnings.length}):`);
    // warnings.forEach((warning, index) => {
    //   console.log(`   ${index + 1}. ${warning}`);
    // });
  }
  
  // Display errors
  if (errors.length > 0) {
    // console.log(`\n❌ Errors (${errors.length}):`);
    errors.forEach((error, index) => {
      // console.error(`   ${index + 1}. ${error}`);
    });
  }
  
  if (!ENV.isProduction) {
    // console.log('=====================================\n');
  }
    
    return {
      isValid: errors.length === 0,
      warnings,
      errors
    };
  } catch (error) {
    // console.warn('⚠️  Configuration validation skipped due to error:', error);
    return {
      isValid: true,
      warnings: ['Configuration validation skipped'],
      errors: []
    };
  }
};

/**
 * Production readiness check
 */
export const checkProductionReadiness = (): boolean => {
  try {
    if (!ENV.isProduction) return true; // Skip check in development
    
    const issues: string[] = [];
    
    // Check for required environment variables
    if (!getEnvVar('VITE_BACKEND_URL')) {
      issues.push('Missing VITE_BACKEND_URL');
    }
    
    if (!getEnvVar('VITE_RAZORPAY_KEY_ID')) {
      issues.push('Missing VITE_RAZORPAY_KEY_ID (required for payments)');
    }
    
    // Check if backend URL uses HTTPS in production
    if (ENV.isProduction && BACKEND_CONFIG.url.startsWith('http://')) {
      issues.push('Backend URL should use HTTPS in production');
    }
    
    if (issues.length > 0) {
      console.error('❌ Production Readiness Check Failed:');
      issues.forEach(issue => console.error(`   • ${issue}`));
      console.error('\nPlease configure these environment variables before deploying to production.');
      return false;
    }
    
    // console.log('✅ Production readiness check passed');
    return true;
  } catch (error) {
    // console.warn('⚠️  Production readiness check skipped due to error:', error);
    return true; // Don't block app from running
  }
};

// Export everything as default as well
export default {
  ENV,
  BACKEND_CONFIG,
  DEMO_MODE_CONFIG,
  PAYMENT_CONFIG,
  FIREBASE_CONFIG,
  APP_CONFIG,
  LOGGING_CONFIG,
  validateConfig,
  checkProductionReadiness
};